import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiUser, FiShoppingBag, FiTruck, FiAlertTriangle, FiCalendar } = FiIcons;

const KitchenOrderCard = ({ order, onStatusChange, priority, settings }) => {
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    const updateElapsedTime = () => {
      const now = new Date();
      const orderTime = new Date(order.createdAt || order.created_at);

      // If order is completed, calculate time from creation to completion
      if (order.status === 'completed' && order.completedAt) {
        const completedTime = new Date(order.completedAt || order.completed_at);
        const diffInSeconds = Math.floor((completedTime - orderTime) / 1000);
        setElapsedTime(diffInSeconds);
        return; // Don't continue updating if completed
      }

      // For active orders, calculate time from creation to now
      if (order.status !== 'completed') {
        const diffInSeconds = Math.floor((now - orderTime) / 1000);
        setElapsedTime(diffInSeconds);
      }
    };

    updateElapsedTime();

    // Only set interval for non-completed orders
    if (order.status !== 'completed') {
      const timer = setInterval(updateElapsedTime, 1000);
      return () => clearInterval(timer);
    }
  }, [order.createdAt, order.created_at, order.status, order.completedAt, order.completed_at]);

  const getPriorityClasses = () => {
    switch (priority) {
      case 'overdue':
        return 'bg-red-900/80 border-red-500';
      case 'urgent':
        return 'bg-yellow-900/80 border-yellow-500';
      default:
        return 'bg-gray-800 border-gray-700';
    }
  };

  const getTimerColor = () => {
    if (order.status === 'completed') return 'text-green-400';
    const minutes = Math.floor(elapsedTime / 60);
    if (minutes >= settings.overdueTime) return 'text-red-400';
    if (minutes >= settings.warningTime) return 'text-yellow-400';
    return 'text-gray-300';
  };

  const formatElapsedTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getNextStatus = () => {
    switch (order.status) {
      case 'pending':
        return 'preparing';
      case 'preparing':
        return 'ready';
      case 'ready':
        return 'completed';
      default:
        return null;
    }
  };

  const getStatusButtonText = () => {
    switch (order.status) {
      case 'pending':
        return 'Start';
      case 'preparing':
        return 'Ready';
      case 'ready':
        return 'Complete';
      default:
        return null;
    }
  };

  const getStatusButtonColor = () => {
    switch (order.status) {
      case 'pending':
        return 'bg-blue-600 hover:bg-blue-500';
      case 'preparing':
        return 'bg-orange-600 hover:bg-orange-500';
      case 'ready':
        return 'bg-green-600 hover:bg-green-500';
      default:
        return 'bg-gray-600 hover:bg-gray-500';
    }
  };

  const formatPickupTime = () => {
    const customerInfo = order.customerInfo || order.customer_info || {};
    
    if (customerInfo.pickupTime && customerInfo.pickupTime !== 'asap') {
      const pickupDate = new Date(customerInfo.pickupTime);
      return pickupDate.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric'
      });
    }
    
    return 'ASAP';
  };

  const customerInfo = order.customerInfo || order.customer_info || {};
  const orderItems = order.items || [];
  const orderType = customerInfo.orderType || order.order_type || 'pickup';

  return (
    <div className={`rounded-lg border-2 ${getPriorityClasses()} flex flex-col h-full shadow-lg`}>
      {/* Header with Large Order Number */}
      <div className="p-3 border-b-2 border-gray-700/50">
        {/* Large Order Number - Center */}
        <div className="text-center mb-3">
          <h2 className="text-4xl font-black text-white tracking-wider">
            #{order.orderNumber || order.order_number}
          </h2>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {orderType === 'pickup' ? (
              <span className="bg-purple-800 text-purple-300 px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                <SafeIcon icon={FiShoppingBag} />
                PICKUP
              </span>
            ) : (
              <span className="bg-blue-800 text-blue-300 px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                <SafeIcon icon={FiTruck} />
                DELIVERY
              </span>
            )}
          </div>
          <div className={`font-mono text-2xl font-bold ${getTimerColor()}`}>
            {formatElapsedTime(elapsedTime)}
            {order.status === 'completed' && (
              <span className="text-xs block text-green-400">FINAL</span>
            )}
          </div>
        </div>

        {/* Customer Info */}
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-400">
          <SafeIcon icon={FiUser} />
          <span>{customerInfo.name}</span>
          {priority === 'overdue' && order.status !== 'completed' && (
            <SafeIcon icon={FiAlertTriangle} className="text-red-400 animate-pulse" />
          )}
        </div>

        {/* Pickup/Delivery Time */}
        <div className="flex items-center gap-2 mt-1 text-sm">
          <SafeIcon icon={orderType === 'pickup' ? FiCalendar : FiTruck} className="text-gray-400" />
          <span className="text-yellow-300 font-medium">
            {orderType === 'pickup' ? 'Pickup' : 'Delivery'}: {formatPickupTime()}
          </span>
        </div>
      </div>

      {/* Items */}
      <div className="p-3 space-y-2 flex-1 overflow-y-auto">
        {orderItems.map((item, index) => (
          <div key={index} className="bg-gray-900/50 p-2 rounded-md">
            <p className="font-bold text-white">
              <span className="text-xl mr-2">{item.quantity}x</span>
              {item.name}
            </p>
            {item.customizations && (
              <div className="pl-6 text-sm text-gray-400 space-y-0.5 mt-1">
                {item.customizations.bread && <p>• Bread: {item.customizations.bread}</p>}
                {item.customizations.protein && <p>• Protein: {item.customizations.protein}</p>}
                {item.customizations.toppings?.length > 0 && (
                  <p>• Toppings: {item.customizations.toppings.join(', ')}</p>
                )}
                {item.customizations.extras?.length > 0 && (
                  <p className="text-yellow-400">• Extras: {item.customizations.extras.join(', ')}</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Special Instructions */}
      {customerInfo.specialInstructions && (
        <div className="p-3 border-t border-gray-700/50">
          <div className="bg-yellow-900/50 border border-yellow-700 rounded-md p-2">
            <h4 className="font-bold text-yellow-400 text-sm mb-1">⚠️ Special Instructions:</h4>
            <p className="text-sm text-yellow-300">{customerInfo.specialInstructions}</p>
          </div>
        </div>
      )}

      {/* Actions */}
      {getNextStatus() && (
        <div className="p-2">
          <button
            onClick={() => onStatusChange(order.id, getNextStatus())}
            className={`w-full ${getStatusButtonColor()} text-white py-3 rounded-lg font-bold text-lg transition-colors`}
          >
            {getStatusButtonText()}
          </button>
        </div>
      )}
    </div>
  );
};

export default KitchenOrderCard;